import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { Loader, Toggle } from "rsuite";

import { useAuth } from "context/auth-context";
import { useConfig } from "../context/config-context";
import { useModal } from "../context/modal-context";

import Gabarit from "../components/Gabarit";
import AlertBanner from "../components/AlertBanner";
import { InputConfirmPassword, InputPassword } from "../components/Inputs";
import UpdateMobilePhoneModal from "../components/Modal/UpdateMobilePhoneModal";

import API from "../utils/api";

export default function AccountPage() {
  const [loading, setLoading] = useState(false);
  const [loadingPreferences, setLoadingPreferences] = useState(false);
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);
  const [initialNotificationsEnabled, setInitialNotificationsEnabled] = useState(true);
  const [successMessage, setSuccessMessage] = useState(null);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm();

  const siteConfig = useConfig();
  const { user, refreshUser } = useAuth();
  const { showModal, setModalLabel } = useModal();

  const password = watch("password");

  // Charger les préférences de notifications au montage
  useEffect(() => {
    if (user?.role === "prescriber") {
      fetchNotificationPreferences();
    }
  }, [user]);

  /**
   * Récupère les préférences de notifications du prescripteur
   */
  const fetchNotificationPreferences = async () => {
    try {
      const response = await API.get("/prescriber/preferences");
      if (response.data.success) {
        const enabled = response.data.data.documentNotificationPreferences?.enabled ?? true;
        setNotificationsEnabled(enabled);
        setInitialNotificationsEnabled(enabled);
      }
    } catch (error) {
      console.error("Erreur récupération préférences:", error);
    }
  };

  /**
   * Met à jour l'état local du toggle (sans sauvegarder)
   */
  const handleToggleChange = (checked) => {
    setNotificationsEnabled(checked);
  };

  /**
   * Enregistre les préférences de notifications
   */
  const handleSaveNotificationPreferences = async () => {
    try {
      setLoadingPreferences(true);

      const response = await API.put("/prescriber/preferences", {
        documentNotificationPreferences: {
          enabled: notificationsEnabled,
        },
      });

      if (response.data.success) {
        setInitialNotificationsEnabled(notificationsEnabled);
        setSuccessMessage("Vos préférences de notification ont bien été enregistrées.");
        window.scrollTo({ top: 0, behavior: "smooth" });

        // Masquer le message après 5 secondes
        setTimeout(() => setSuccessMessage(null), 5000);
      }
    } catch (error) {
      console.error("Erreur mise à jour préférences:", error);
    } finally {
      setLoadingPreferences(false);
    }
  };

  /**
   * Soumet le formulaire de changement de mot de passe
   */
  const onSubmitPassword = async (data) => {
    try {
      setLoading(true);
      await API.put("/users/reset-password", data);
      setSuccessMessage("Votre mot de passe a bien été renouvelé.");
      window.scrollTo({ top: 0, behavior: "smooth" });

      // Masquer le message après 5 secondes
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (error) {
      // Les erreurs sont gérées par l'interceptor axios (toast)
    } finally {
      setLoading(false);
    }
  };

  /**
   * Ouvre la modale de modification du téléphone mobile
   */
  const handleEditMobilePhone = () => {
    setModalLabel("");
    showModal(
      <UpdateMobilePhoneModal
        currentPhone={user.mobilePhone}
        onSuccess={(newPhone) => {
          // Rafraîchir les données utilisateur
          refreshUser();
          setSuccessMessage("Le numéro de téléphone mobile a bien été mis à jour.");
          window.scrollTo({ top: 0, behavior: "smooth" });
          setTimeout(() => setSuccessMessage(null), 5000);
        }}
      />
    );
  };

  return (
    <Gabarit
      label={siteConfig.pages.account.title}
      className="flex flex-col space-y-2"
    >
      {/* Banner de succès */}
      {successMessage && (
        <AlertBanner
          type="success"
          message={successMessage}
          onClose={() => setSuccessMessage(null)}
          className="mb-4"
        />
      )}

      {/* Layout en 3 colonnes (1/3 + 2/3) */}
      <div className="grid gap-6 lg:grid-cols-3">
        {/* Colonne gauche : Informations du compte (1/3) */}
        <div className="flex flex-col p-5 h-fit space-y-4 bg-white shadow-lg rounded-xl">
          <h2 className="text-lg font-semibold text-primary border-b pb-2">
            Informations du compte
          </h2>

          <div className="space-y-3">
            {/* Nom et Prénom */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-base font-semibold text-neutral-dark block mb-1">
                  Nom
                </label>
                <p className="text-base text-neutral-dark">{user.lastName}</p>
              </div>
              <div>
                <label className="text-base font-semibold text-neutral-dark block mb-1">
                  Prénom
                </label>
                <p className="text-base text-neutral-dark">{user.firstName}</p>
              </div>
            </div>

            {/* Email */}
            <div>
              <label className="text-base font-semibold text-neutral-dark block mb-1">
                Email
              </label>
              <p className="text-base text-neutral-dark">{user.email}</p>
            </div>

            {/* Adresse */}
            <div>
              <label className="text-base font-semibold text-neutral-dark block mb-1">
                Adresse
              </label>
              <p className="text-base text-neutral-dark">
                {user.location?.street &&
                user.location?.zipCode &&
                user.location?.city
                  ? `${user.location.street}, ${user.location.zipCode} ${user.location.city}`
                  : "- -"}
              </p>
            </div>

            {/* Téléphones */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-base font-semibold text-neutral-dark block mb-1">
                  Téléphone fixe
                </label>
                <p className="text-base text-neutral-dark">
                  {user.linePhone || "- -"}
                </p>
              </div>
              <div>
                <label className="text-base font-semibold text-neutral-dark block mb-1">
                  Téléphone mobile
                </label>
                <div className="flex items-center w-fit gap-2">
                  <p className="text-base text-neutral-dark flex-1">
                    {user.mobilePhone || "- -"}
                  </p>
                  {user?.role === "prescriber" && (
                    <button
                      onClick={handleEditMobilePhone}
                      className="text-primary hover:text-primary/70 transition-colors"
                      title="Modifier le numéro de téléphone mobile"
                    >
                      <img
                        src={siteConfig.medias.edit_svg.path}
                        alt="Modifier"
                        width={18}
                        height={18}
                      />
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Message d'information pour les prescripteurs */}
            {user?.role === "prescriber" && (
              <p className="text-sm italic text-neutral-dark pt-2">
                Les numéros de téléphone renseignés sont utilisés exclusivement
                dans le cadre du portail prescripteur et ne font l'objet
                d'aucune autre utilisation.
              </p>
            )}
          </div>
        </div>

        {/* Colonne droite : Préférences + Mot de passe (2/3) */}
        <div className="flex flex-col gap-6 lg:col-span-2">
          {/* Préférences de notifications (prescripteurs uniquement) */}
          {user?.role === "prescriber" && (
            <div className="flex flex-col p-5 space-y-4 h-fit bg-white shadow-lg rounded-xl">
              <h2 className="text-lg font-semibold text-primary border-b pb-2">
                Préférences de notifications
              </h2>

              <div className="space-y-3">
                <p className="text-sm text-neutral-dark">
                  Vous avez la possibilité d'activer ou de désactiver la
                  notification par mail pour les mails suivants :
                </p>

                {/* Section Nouveaux documents */}
                <div className="pt-1">
                  <h3 className="text-base font-semibold text-neutral-dark mb-2.5">
                    Nouveaux documents
                  </h3>

                  <div className="flex items-center gap-2 mb-2">
                    <label className="text-sm text-neutral-dark">
                      Recevoir un mail pour la mise à disposition de nouveaux
                      documents de votre file active :{" "}
                      <span className="font-semibold mr-2">
                        {notificationsEnabled ? "Activé" : "Désactivé"}
                      </span>
                      <Toggle
                        checked={notificationsEnabled}
                        onChange={handleToggleChange}
                        disabled={loadingPreferences}
                      />
                    </label>
                  </div>
                </div>

                {/* Bouton d'enregistrement */}
                <div className="flex justify-end pt-4 border-t border-t-gray-200">
                  <button
                    onClick={handleSaveNotificationPreferences}
                    disabled={
                      loadingPreferences ||
                      notificationsEnabled === initialNotificationsEnabled
                    }
                    className="px-[12.5px] py-[6px] bg-primary text-white rounded hover:bg-primary/90 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors font-semibold"
                  >
                    {loadingPreferences ? (
                      <Loader />
                    ) : (
                      "Enregistrer les préférences"
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Renouvellement du mot de passe */}
          <div className="flex flex-col p-5 space-y-4 h-fit bg-white shadow-lg rounded-xl">
            <h2 className="text-lg font-semibold text-primary border-b pb-2">
              Renouvellement du mot de passe
            </h2>

            <form
              onSubmit={handleSubmit(onSubmitPassword)}
              className="space-y-4"
            >
              <div className="grid grid-cols-2 gap-4">
                <InputPassword
                  id="password"
                  label="Nouveau mot de passe"
                  placeholder="Nouveau mot de passe"
                  register={register}
                  error={errors.password}
                />
                <InputConfirmPassword
                  id="confirmPassword"
                  label="Confirmer le mot de passe"
                  placeholder="Confirmer le mot de passe"
                  password={password}
                  register={register}
                  error={errors.confirmPassword}
                />
              </div>

              <div className="flex justify-end pt-4 border-t border-t-gray-200">
                <button
                  type="submit"
                  className="px-[12.5px] py-[6px] bg-primary text-white rounded hover:bg-primary/90 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors font-semibold"
                  disabled={
                    loading || !watch("password") || !watch("confirmPassword")
                  }
                >
                  {loading ? <Loader /> : "Valider"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </Gabarit>
  );
}
